import React, { useRef, useState, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Trash2, Download, RotateCcw } from 'lucide-react';

interface SignatureCaptureProps {
  onSignatureChange: (signatureData: string | null) => void;
  isRequired?: boolean;
  className?: string;
}

export const SignatureCapture: React.FC<SignatureCaptureProps> = ({
  onSignatureChange,
  isRequired = false,
  className = '',
}) => {
  const signatureRef = useRef<SignatureCanvas>(null);
  const [hasSignature, setHasSignature] = useState(false);

  const clearSignature = useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setHasSignature(false);
      onSignatureChange(null);
    }
  }, [onSignatureChange]);

  const saveSignature = useCallback(() => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureData = signatureRef.current.toDataURL('image/png');
      setHasSignature(true);
      onSignatureChange(signatureData);
    } else {
      setHasSignature(false);
      onSignatureChange(null);
    }
  }, [onSignatureChange]);

  const handleBeginStroke = useCallback(() => {
    // Clear any previous signature when user starts drawing
    if (hasSignature) {
      setHasSignature(false);
      onSignatureChange(null);
    }
  }, [hasSignature, onSignatureChange]);

  const handleEndStroke = useCallback(() => {
    // Auto-save signature when user finishes drawing
    setTimeout(saveSignature, 100);
  }, [saveSignature]);

  const downloadSignature = useCallback(() => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const link = document.createElement('a');
      link.download = 'signature.png';
      link.href = signatureRef.current.toDataURL('image/png');
      link.click();
    }
  }, []);

  return (
    <div className={`space-y-4 ${className}`} dir="rtl">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          التوقيع {isRequired && <span className="text-red-500">*</span>}
        </label>
        <div className="flex items-center space-x-2 space-x-reverse">
          <button
            type="button"
            onClick={clearSignature}
            className="p-2 text-gray-500 hover:text-red-600 transition-colors"
            title="مسح التوقيع"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={downloadSignature}
            disabled={!hasSignature}
            className="p-2 text-gray-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="تحميل التوقيع"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={clearSignature}
            className="p-2 text-gray-500 hover:text-orange-600 transition-colors"
            title="إعادة تعيين"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
        <SignatureCanvas
          ref={signatureRef}
          canvasProps={{
            className: 'w-full h-48 cursor-crosshair',
            style: {
              direction: 'ltr', // Canvas should be LTR for proper drawing
            },
          }}
          backgroundColor="white"
          penColor="black"
          onBegin={handleBeginStroke}
          onEnd={handleEndStroke}
        />
      </div>

      <div className="text-sm text-gray-500 text-center">
        {hasSignature ? (
          <span className="text-green-600">✓ تم حفظ التوقيع</span>
        ) : (
          <span>ارسم توقيعك في المربع أعلاه</span>
        )}
      </div>

      {isRequired && !hasSignature && (
        <p className="text-red-600 text-sm">التوقيع مطلوب لإكمال الاتفاقية</p>
      )}
    </div>
  );
}; 